import { CssBaseline } from '@mui/material';
import { styled, ThemeProvider } from '@mui/material/styles';
import { DataGrid, type DataGridProps } from '@mui/x-data-grid';
import useAppTheme from 'hooks/useAppTheme';
import * as React from 'react';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
import { darkTheme, fontFamily, lightTheme } from './MuiDataGridTheme';

interface ThemeColors {
  headerBg: string;
  footerBg: string;
  rowBg: string;
  evenRowBg: string;
  hoverBg: string;
  borderColor: string;
  selectedBg: string;
  selectedHoverBg: string;
  editingRowBg: string;
  checkboxCheckedColor: string;
}

const headerBgDark = clientThemePalette[CLIENT_THEME].dark.tableHeader;
const headerBgLight = clientThemePalette[CLIENT_THEME].light.tableHeader;
const isYokohama = CLIENT_THEME === 'yokohama';
const rowBgDark = isYokohama ? '#111111' : '#111728';
const evenRowBgDark = isYokohama ? '#181818' : '#1C2331';
const hoverBgDark = isYokohama ? '#1f1f1f' : '#1E293B';

const StyledDataGrid = styled(DataGrid, {
  shouldForwardProp: (prop) => prop !== 'isDarkMode' && prop !== 'themeColors',
})<{ isDarkMode: boolean; themeColors: ThemeColors }>(
  ({ theme, isDarkMode, themeColors }) => ({
    border: 'none',
    fontFamily,
    fontSize: 14,
    '--header-bg-dark': themeColors.headerBg,
    '--header-bg-light': '#354A6E',
    '--even-row-bg-light': '#f3f4f6',
    '--even-row-bg-dark': themeColors.evenRowBg,
    '--row-bg-light': '#ffffff',
    '--row-bg-dark': themeColors.rowBg,
    '--hover-bg-light': '#f0f0f0',
    '--hover-bg-dark': themeColors.hoverBg,
    '--selected-bg-light': '#d0f0c0',
    '--selected-bg-dark': themeColors.selectedBg,
    '--selected-hover-bg-dark': themeColors.selectedHoverBg,
    '--cell-text-light': '#111111',
    '--cell-text-dark': '#E2E8F0',
    '--border-color-light': '#e0e0e0',
    '--border-color-dark': themeColors.borderColor,
    '--editing-row-bg-light': '#e3f2fd',
    '--editing-row-bg-dark': themeColors.editingRowBg,
    '--editing-cell-bg-light': '#e0f7fa',
    '--editing-cell-bg-dark': '#4A5568',
    '--editing-cell-glow-dark': themeColors.checkboxCheckedColor,
    '--non-editing-cell-text-dark': '#A0AEC0',
    '--footer-bg-light': '#f5f5f5',
    '--footer-bg-dark': themeColors.footerBg,
    '--footer-text-light': '#111111',
    '--footer-text-dark': '#E2E8F0',
    '--checkbox-color-dark': '#A0AEC0',
    '--checkbox-checked-color-dark': themeColors.checkboxCheckedColor,

    backgroundColor: isDarkMode ? 'var(--row-bg-dark)' : 'var(--row-bg-light)',

    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: isDarkMode
        ? 'var(--header-bg-dark)'
        : 'var(--header-bg-light)',
      color: '#ffffff',
      fontWeight: 'bold',
      border: 'none',
    },

    '& .MuiDataGrid-columnSeparator': {
      color: 'transparent',
    },
    '& .MuiDataGrid-columnSeparator--resizable:hover, & .MuiDataGrid-columnSeparator--resizing':
      {
        color: isDarkMode ? '#90caf9' : '#ffffff',
        opacity: 1,
      },

    '& .MuiDataGrid-cell': {
      color: isDarkMode ? 'var(--cell-text-dark)' : 'var(--cell-text-light)',
      borderBottom: `1px solid ${
        isDarkMode ? 'var(--border-color-dark)' : 'var(--border-color-light)'
      }`,
    },

    '& .MuiDataGrid-row': {
      backgroundColor: isDarkMode
        ? 'var(--row-bg-dark)'
        : 'var(--row-bg-light)',
      '&:hover': {
        backgroundColor: isDarkMode
          ? 'var(--hover-bg-dark)'
          : 'var(--hover-bg-light)',
      },
      '&:nth-of-type(even)': {
        backgroundColor: isDarkMode
          ? 'var(--even-row-bg-dark)'
          : 'var(--even-row-bg-light)',
      },
      // Selected row style
      '&.Mui-selected': {
        backgroundColor: `${
          isDarkMode ? 'var(--selected-bg-dark)' : 'var(--selected-bg-light)'
        } !important`, // Important to override other BGs
        color: `${
          isDarkMode ? '#FFFFFF' : 'var(--cell-text-light)'
        } !important`,
        '&:hover': {
          backgroundColor: `${
            isDarkMode
              ? 'var(--selected-hover-bg-dark)'
              : 'var(--selected-bg-light)'
          } !important`, // Slightly darker/different shade on hover for selected
        },
      },
    },

    // Row is in edit mode
    '& .MuiDataGrid-row.MuiDataGrid-row--editing': {
      backgroundColor: isDarkMode
        ? 'var(--editing-row-bg-dark)'
        : 'var(--editing-row-bg-light)',
      boxShadow: isDarkMode
        ? '0 0 10px rgba(0,0,0,0.3)'
        : '0 0 10px rgba(0,0,0,0.1)', // Subtle shadow to lift editing row
      '&:hover': {
        // Ensure hover on editing row is consistent or slightly different
        backgroundColor: isDarkMode
          ? 'var(--editing-row-bg-dark)'
          : 'var(--editing-row-bg-light)',
      },
    },

    // Editable cell in an editing row
    '& .MuiDataGrid-cell.MuiDataGrid-cell--editing': {
      backgroundColor: isDarkMode
        ? 'var(--editing-cell-bg-dark)'
        : 'var(--editing-cell-bg-light)',
      color: isDarkMode ? '#FFFFFF' : '#000000', // Ensure text is readable
      boxShadow: isDarkMode
        ? `inset 0 0 0 2px var(--editing-cell-glow-dark)`
        : `inset 0 0 0 2px ${theme.palette.primary.main}`, // Visual cue for active edit
    },

    '& .MuiInputBase-root.MuiDataGrid-editInputCell': {
      color: isDarkMode ? '#FFFFFF' : '#000000', // Ensure text is readable
    },

    // Non-editable cell in an editing row
    '& .MuiDataGrid-row.MuiDataGrid-row--editing .MuiDataGrid-cell:not(.MuiDataGrid-cell--editing)':
      {
        backgroundColor: 'transparent', // Inherit from editing row for consistency
        color: isDarkMode ? 'var(--non-editing-cell-text-dark)' : '#757575', // Dimmed text
      },

    '& .MuiDataGrid-footerContainer': {
      backgroundColor: isDarkMode
        ? 'var(--footer-bg-dark)'
        : 'var(--footer-bg-light)',
      color: isDarkMode
        ? 'var(--footer-text-dark)'
        : 'var(--footer-text-light)',
      borderTop: `1px solid ${
        isDarkMode ? 'var(--border-color-dark)' : 'var(--border-color-light)'
      }`,
    },

    // Pagination styles
    '& .MuiTablePagination-root, & .MuiDataGrid-pagination': {
      color: isDarkMode
        ? 'var(--footer-text-dark)'
        : 'var(--footer-text-light)',
      fontWeight: 'normal', // Default Mui is bold, can be adjusted
      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
        {
          color: isDarkMode
            ? 'var(--footer-text-dark)'
            : 'var(--footer-text-light)',
        },
      '& .MuiIconButton-root': {
        color: isDarkMode
          ? 'var(--footer-text-dark)'
          : 'var(--footer-text-light)',
        '&.Mui-disabled': {
          color: isDarkMode ? '#718096' : '#A0AEC0', // Dimmed color for disabled icons
        },
      },
      '& .MuiSvgIcon-root': {
        // Ensure icons in pagination also get the color
        color: 'inherit',
      },
    },

    '& .MuiCheckbox-root': {
      color: `${isDarkMode ? 'var(--checkbox-color-dark)' : '#666'} !important`,
      '&.Mui-checked': {
        color: `${
          isDarkMode
            ? 'var(--checkbox-checked-color-dark)'
            : theme.palette.primary.main
        } !important`,
      },
    },

    // Style for the "No Rows" overlay
    '& .MuiDataGrid-overlayWrapper, & .MuiDataGrid-overlay': {
      backgroundColor: isDarkMode
        ? `${themeColors.rowBg}CC`
        : 'rgba(255, 255, 255, 0.8)',
      color: isDarkMode ? 'var(--cell-text-dark)' : 'var(--cell-text-light)',
    },
  })
);

export interface CustomDataGridProps extends DataGridProps {}

const CustomStyledDataGrid = React.forwardRef<
  HTMLDivElement,
  CustomDataGridProps
>((props, ref) => {
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  const themeColors: ThemeColors = {
    headerBg: palette.tableHeader,
    footerBg: palette.toolBarBackground,
    rowBg: palette.pageBackground,
    evenRowBg: palette.boxBackground,
    hoverBg: `${palette.primary}1A`,
    borderColor: palette.cardBorderColor,
    selectedBg: `${palette.primary}40`,
    selectedHoverBg: `${palette.primary}59`,
    editingRowBg: palette.borderGrey,
    checkboxCheckedColor: palette.primary,
  };

  return (
    <ThemeProvider theme={enableDarkTheme ? darkTheme : lightTheme}>
      <CssBaseline />
      <StyledDataGrid
        ref={ref}
        {...props}
        isDarkMode={enableDarkTheme}
        hideFooter={true}
        themeColors={themeColors}
      />
    </ThemeProvider>
  );
});

CustomStyledDataGrid.displayName = 'CustomDataGrid';

export default CustomStyledDataGrid;
